import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import { ResponsiveContainer, LineChart, XAxis, YAxis, Line, CartesianGrid, Tooltip } from 'recharts';
import './AlertsChart.less';


const CustomAxisTick = (props) => {
  const { x, y, payload } = props;

  const formattedDate = moment(payload.value).format('MMM D (LT)');

  return (
    <g transform={`translate(${x},${y})`}>
      <text x={0} y={0} dy={16} dx={28} textAnchor="end" fill="#666">
        {formattedDate}
      </text>
    </g>
  );
};

function restructureData(formattedDataPoints) {
  const keys = Object.keys(formattedDataPoints);
  const dataArray = [];
  let longest = 0;
  let longestData = [];

  // create initial object with all keys set to null
  const initializeDataPoints = keys.reduce((o, key) => {
    return {
      ...o,
      [key]: null,
      [`${key}__gap`]: null,
    };
  }, {});

  // find the data point with the most data points
  keys.forEach((reg) => {
    if (formattedDataPoints[reg].length > longest) {
      longestData = formattedDataPoints[reg];
      longest = formattedDataPoints[reg].length;
    }
  });

  // add initialized registers with null data, longest data set and dates to dataArray
  longestData.forEach((dataset) => {
    dataArray.push({
      ...initializeDataPoints,
      ...dataset,
    });
  });

  // go through the registers and replace null values if applicable
  keys.forEach((register) => {
    const values = formattedDataPoints[register];
    values.forEach((dataset, index) => {
      if (formattedDataPoints[register].length === longest) {
        // If the data length is the same as the longest data length
        // ie. there is no missing data
        dataArray[index][register] = dataset[register];
      } else {
        const indexOfDate = dataArray.findIndex((element) => {
          const { _i: elDateFormat } = element.date;
          const { _i: datasetDateFormat } = dataset.date;
          return elDateFormat === datasetDateFormat;
        });
        dataArray[indexOfDate][register] = dataset[register];
      }
    });
  });

  /* eslint-disable no-param-reassign */
  keys.forEach((register) => {
    const gapKey = `${register}__gap`;

    const firstValidRow = dataArray.find((row) => row[register] != null);
    let lastValue = firstValidRow ? firstValidRow[register] : null;

    dataArray.forEach((row) => {
      const value = row[register];

      if (value != null) {
        lastValue = value;
        row[gapKey] = value;
      } else {
        row[gapKey] = lastValue;
      }
    });
  });
  
  return dataArray;
}

export const AlertsChart = (props) => {
  const { data, theme, colorObj } = props;
  const [formattedData, setFormmattedData] = useState({});
  const [dataPoints, setDataPoints] = useState([]);

  useEffect(() => {
    const formattedDataPoints = {};
    const formattedTags = [];

    data.data_points.map((dataSet) => {
      return formattedTags.push(dataSet.id);
    });

    data.data_points.map((dataSet) => {
      formattedDataPoints[dataSet.id] = dataSet.graph_data.map((d) => ({
        date: d.x,
        [dataSet.id]: d.actual,
      }));

      return formattedDataPoints;
    });

    setDataPoints(formattedTags);
    setFormmattedData(restructureData(formattedDataPoints));
  }, [data]);

  const tooltipFormatter = (value, name) => {
    let metric = '';
    let displayName = '';

    if (data.data_points) {
      data.data_points.forEach((datapoint) => {
        if (datapoint.id === name) {
          metric = datapoint.unit;
          displayName = datapoint.name;
        }
      });
    }

    return (
      <div>
        {displayName}
        : 
        <span className='tt-value'>
          {value !== null ? value.toFixed(2) : <span className='tt-offline'>DISCONNECTED</span>}
          <span className='tt-unit'>
            {value ? metric : ''}
          </span>
        </span>
      </div>)
  };

  const CustomTooltip = ({ active, label, payload }) => {
      if (!active || !payload?.length) return null;
  
      return (
        <div className={`tooltip tooltip-${theme}`}>
          <div className="tooltip-date">{moment(label).format('MMM D, YYYY (LT)')}</div>
  
          {payload.map((item) => (
            item.dataKey.includes('__gap') ?
            null
            :
            <div key={item.dataKey} className="tt-datapoint">
              <span
                style={{
                  background: item.color ?? item.stroke ?? item.payload?.stroke,
                }}
                className="tt-circle"
              />
              <div>{tooltipFormatter(item.value, item.dataKey)}</div>
            </div>
          ))}
        </div>
      );
    };
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={formattedData}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis type="category" height={80} dataKey="date" tick={<CustomAxisTick />} />
        <YAxis type="number" />
        {dataPoints.map((dataPoint) => {
          return (
            // <Line key={dataPoint} dataKey={dataPoint} stroke={colorObj[dataPoint]} dot={false} strokeWidth={2} />
            <React.Fragment key={dataPoint}>
              <Line
                dataKey={dataPoint}
                stroke={colorObj[dataPoint]}
                dot={({ cx, cy, index }) => {
                  const current = formattedData[index]?.[dataPoint];
                  const previous = formattedData[index - 1]?.[dataPoint];
                  const next = formattedData[index + 1]?.[dataPoint];
  
                  const isIsolatedPoint =
                    current != null &&
                    previous == null &&
                    next == null;
  
                  if (!isIsolatedPoint) return null;
  
                  return (
                    <circle
                      cx={cx}
                      cy={cy}
                      r={2.5}
                      fill={colorObj[dataPoint]}
                      stroke={colorObj[dataPoint]}
                    />
                  );
                }}
                strokeWidth={2}
                connectNulls={false}
              />
  
              <Line
                dataKey={`${dataPoint}__gap`}
                stroke={`${colorObj[dataPoint]}66`}
                strokeWidth={1.5}
                strokeDasharray="5 5"
                connectNulls={false}
                legendType="none"
                dot={false}
              />
            </React.Fragment>
          );
        })}
        <Tooltip
          content={<CustomTooltip />}
          isAnimationActive={false}
          filterNull={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
};

AlertsChart.propTypes = {
  data: PropTypes.shape({
    date: PropTypes.string,
    interval: PropTypes.string,
    name: PropTypes.string,
    slug: PropTypes.string,
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        graph_data: PropTypes.arrayOf(
          PropTypes.shape({
            actual: PropTypes.number,
            x: PropTypes.string,
          }),
        ),
        id: PropTypes.string,
        name: PropTypes.string,
        tag: PropTypes.string,
        unit: PropTypes.string,
      }),
    ),
  }).isRequired,
};
